/**
 * Note AI Edit Chat Slice
 *
 * Redux slice for managing chat state, messages, and interrupt workflow
 */

// Import shared types from ai-chat-core
import type {
  ActivityMessage,
  ActivityType,
  AssistantStatus,
  ChatMessage,
  ChoiceOption,
  ConfirmationNamespace,
  InterruptEventType,
  SSEPerformanceMetrics,
  StepPayload,
  StepResponse,
} from '@features/ai-chat-core';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// Import note-specific types
import type { ConfirmationType, EditPreview } from '../types';

// Re-export shared types for backward compatibility
export type { SSEPerformanceMetrics, StepResponse } from '@features/ai-chat-core';

// ============================================================================
// State Interface
// ============================================================================

/**
 * Note-specific interrupt state
 * Uses note-specific EditPreview and ConfirmationType
 */
export interface InterruptState {
  isActive: boolean;
  interruptId: string; // Required for AG-UI resume request
  eventType: InterruptEventType; // Discriminator: 'CONFIRMATION' | 'MULTIPLE_CHOICE' | 'MULTI_STEP'

  // CONFIRMATION fields
  confirmLabel?: string; // e.g., "Accept"
  cancelLabel?: string; // e.g., "Reject"
  message?: string; // Confirmation message
  edits?: EditPreview[]; // Note-specific edit preview with section_id
  namespace?: ConfirmationNamespace; // Where to show confirmation UI ('global' | 'chat')
  confirmationType?: ConfirmationType; // Note-specific: 'note_changes' | 'train'

  // MULTIPLE_CHOICE fields
  question?: string; // Question for user
  options?: ChoiceOption[]; // Available options
  selectedOptionIds: string[]; // Currently selected option IDs
  allowMultiple?: boolean; // Whether multiple selections allowed
  allowSelectAll?: boolean; // Whether to show "Select All" button
  minSelections?: number; // Minimum selections required
  maxSelections?: number; // Maximum selections allowed
  allowTextInput?: boolean; // Whether to allow free-text input as alternative
  textInputPlaceholder?: string; // Placeholder text for text input

  // MULTI_STEP fields
  steps?: StepPayload[]; // Array of steps in the wizard
  title?: string; // Wizard title
  currentStepIndex?: number; // Current step being displayed (0-indexed)
  stepResponses?: Record<string, StepResponse>; // Responses keyed by step ID

  // EXPRESS_INTEREST fields
  detectedIntent?: string; // Detected intent from user's message
  originalMessage?: string; // Original user message that triggered the interrupt
  metadata?: unknown | null; // Optional metadata
}

export interface NoteAiEditState {
  // Session
  noteId: string | null;
  threadId: string | null;

  // Messages
  messages: ChatMessage[];

  // Activity status messages (messageId -> activity)
  activities: Record<string, ActivityMessage>;

  // Streaming state
  assistant: {
    status: AssistantStatus;
    currentStep: string | null;
    streamingContent: string;
    currentMessageId: string | null;
    firstTokenReceived: boolean;
  };

  // Section streaming updates (section_id -> pending content)
  sectionUpdates: Record<string, string>;

  // Sections currently being streamed (section_ids)
  streamingSections: string[];

  // Section selection for copy functionality
  selectedSectionIdsForCopy: string[];

  // Before-AI-edit snapshot per section (2-level undo: current + previous)
  sectionPreviousContent: Record<string, string>;

  // Sections with pending AI edits (independent of interrupt)
  affectedSectionIds: string[];

  // Track user-edited section content (for copy functionality)
  // Key: section_id, Value: current markdown content
  dirtySections: Record<string, string>;

  // Unified interrupt state
  interrupt: InterruptState | null;

  // UI state
  isChatVisible: boolean;
  isChatOnboardingVisible: boolean;

  // Currently focused/editing section (for sidebar banner)
  focusedSection: { id: string; name: string } | null;

  // Error handling
  error: string | null;

  // SSE performance metrics (for tracking and alerting)
  ssePerformanceMetrics: SSEPerformanceMetrics | null;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: NoteAiEditState = {
  noteId: null,
  threadId: null,
  messages: [],
  activities: {},
  assistant: {
    status: 'idle',
    currentStep: null,
    streamingContent: '',
    currentMessageId: null,
    firstTokenReceived: false,
  },
  sectionUpdates: {},
  streamingSections: [],
  selectedSectionIdsForCopy: [],
  sectionPreviousContent: {},
  affectedSectionIds: [],
  dirtySections: {},
  interrupt: null,
  isChatVisible: true,
  isChatOnboardingVisible: false,
  focusedSection: null,
  error: null,
  ssePerformanceMetrics: null,
};

// ============================================================================
// Slice
// ============================================================================

export const noteAiEditSlice = createSlice({
  name: 'noteAiEdit/ui',
  initialState,
  reducers: {
    // Session management
    initializeChat: (state, action: PayloadAction<{ noteId: string }>) => {
      state.noteId = action.payload.noteId;
      state.threadId = null;
      state.messages = [];
      state.activities = {};
      state.error = null;
      // Reset entire assistant object to initial state
      state.assistant = {
        status: 'idle',
        currentStep: null,
        streamingContent: '',
        currentMessageId: null,
        firstTokenReceived: false,
      };
      // Clear any previous section updates when starting new chat session
      state.sectionUpdates = {};
      state.streamingSections = [];
      state.sectionPreviousContent = {};
      state.affectedSectionIds = [];
      state.dirtySections = {};
      state.selectedSectionIdsForCopy = [];
      state.interrupt = null;
      state.focusedSection = null;
      state.ssePerformanceMetrics = null;
      // NOTE: isChatVisible and isChatOnboardingVisible are intentionally NOT reset
      // as they represent UI preferences that should persist across notes
    },

    setThreadId: (state, action: PayloadAction<string>) => {
      state.threadId = action.payload;
    },

    // Message management
    addUserMessage: (state, action: PayloadAction<string>) => {
      const now = new Date();
      state.messages.push({
        id: `user-${now.getTime()}`,
        role: 'user',
        content: action.payload,
        timestamp: now.toISOString(),
      });
    },

    // Streaming state
    setAssistantStatus: (state, action: PayloadAction<AssistantStatus>) => {
      state.assistant.status = action.payload;
    },

    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.assistant.currentStep = action.payload;
    },

    // Activity management
    setActivitySnapshot: (
      state,
      action: PayloadAction<{
        messageId: string;
        activityType: ActivityType;
        message: string;
        timestamp: number;
        replace?: boolean;
      }>,
    ) => {
      const { messageId, activityType, message, timestamp, replace = true } = action.payload;

      // If replace is false and activity already exists, ignore this update
      if (!replace && state.activities[messageId]) {
        return;
      }

      // Update or create activity
      state.activities[messageId] = {
        messageId,
        activityType,
        message,
        timestamp,
      };
    },

    clearActivity: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      const { [messageId]: _removed, ...remaining } = state.activities;
      state.activities = remaining;
    },

    clearAllActivities: state => {
      state.activities = {};
    },

    startStreamingMessage: (state, action: PayloadAction<string>) => {
      state.assistant.currentMessageId = action.payload;
      state.assistant.streamingContent = '';
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'streaming';
    },

    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.assistant.streamingContent += action.payload;
      if (!state.assistant.firstTokenReceived) {
        state.assistant.firstTokenReceived = true;
      }
    },

    finalizeMessage: state => {
      if (state.assistant.streamingContent) {
        const now = new Date();
        state.messages.push({
          id: state.assistant.currentMessageId || `assistant-${now.getTime()}`,
          role: 'assistant',
          content: state.assistant.streamingContent,
          timestamp: now.toISOString(),
        });
      }
      state.assistant.streamingContent = '';
      state.assistant.currentMessageId = null;
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'thinking';
    },

    // Section streaming
    startSectionStreaming: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      state.sectionUpdates[sectionId] = '';
      // Track this section as actively streaming
      if (!state.streamingSections.includes(sectionId)) {
        state.streamingSections.push(sectionId);
      }
    },

    appendSectionContent: (
      state,
      action: PayloadAction<{ sectionId: string; content: string }>,
    ) => {
      const { sectionId, content } = action.payload;
      if (state.sectionUpdates[sectionId] !== undefined) {
        state.sectionUpdates[sectionId] += content;
      } else {
        state.sectionUpdates[sectionId] = content;
      }
    },

    finalizeSectionUpdate: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      const { [sectionId]: _removed, ...remaining } = state.sectionUpdates;
      state.sectionUpdates = remaining;
    },

    finalizeSectionStreaming: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      // Remove from actively streaming sections
      // Keep content in sectionUpdates for display until workflow completes
      state.streamingSections = state.streamingSections.filter(id => id !== sectionId);
    },

    clearAllSectionUpdates: state => {
      state.sectionUpdates = {};
      state.streamingSections = [];
    },

    // Section snapshot and affected state management
    saveSectionSnapshot: (state, action: PayloadAction<{ sectionId: string; content: string }>) => {
      const { sectionId, content } = action.payload;
      state.sectionPreviousContent[sectionId] = content;
    },

    markSectionAffected: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      if (!state.affectedSectionIds.includes(sectionId)) {
        state.affectedSectionIds.push(sectionId);
      }
    },

    clearAffectedSection: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      state.affectedSectionIds = state.affectedSectionIds.filter(id => id !== sectionId);
      // NOTE: Do NOT clear sectionUpdates here - let it persist until cache refreshes
      // This prevents flash of stale content when backend has already auto-saved
    },

    clearSectionSnapshot: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      const { [sectionId]: _removed, ...remaining } = state.sectionPreviousContent;
      state.sectionPreviousContent = remaining;
    },

    // Dirty sections management (for copy functionality)
    setDirtySection: (state, action: PayloadAction<{ sectionId: string; content: string }>) => {
      state.dirtySections[action.payload.sectionId] = action.payload.content;
    },

    clearDirtySection: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      const { [sectionId]: _removed, ...remaining } = state.dirtySections;
      state.dirtySections = remaining;
    },

    clearAllDirtySections: state => {
      state.dirtySections = {};
    },

    // Unified interrupt actions
    showInterrupt: (
      state,
      action: PayloadAction<{
        interruptId: string;
        eventType: InterruptEventType;
        // CONFIRMATION fields
        confirmLabel?: string;
        cancelLabel?: string;
        message?: string;
        edits?: EditPreview[];
        namespace?: ConfirmationNamespace;
        confirmationType?: ConfirmationType;
        // MULTIPLE_CHOICE fields
        question?: string;
        options?: ChoiceOption[];
        allowMultiple?: boolean;
        allowSelectAll?: boolean;
        minSelections?: number;
        maxSelections?: number;
        allowTextInput?: boolean;
        textInputPlaceholder?: string;
        // MULTI_STEP fields
        steps?: StepPayload[];
        title?: string;
        // EXPRESS_INTEREST fields
        detectedIntent?: string;
        originalMessage?: string;
        metadata?: unknown | null;
      }>,
    ) => {
      const { interruptId, eventType, ...fields } = action.payload;

      // Only clear sectionUpdates when a NEW workflow starts with fresh edits
      // Do NOT clear for "train" confirmations that follow note_changes approvals,
      // as this causes a flash of stale content before the cache is updated.
      // sectionUpdates will naturally be cleared when:
      // - A new chat session starts (initializeChat)
      // - Workflow completes successfully (completeWorkflow triggers cache invalidation)
      // - An error occurs (setError)

      state.interrupt = {
        isActive: true,
        interruptId,
        eventType,
        selectedOptionIds: [],
        currentStepIndex: eventType === 'MULTI_STEP' ? 0 : undefined,
        stepResponses: eventType === 'MULTI_STEP' ? {} : undefined,
        ...fields,
      };

      // Set appropriate assistant status based on interrupt type
      if (eventType === 'CONFIRMATION') {
        state.assistant.status = 'awaiting_confirmation';
      } else if (eventType === 'MULTI_STEP') {
        state.assistant.status = 'awaiting_selection';
      } else if (eventType === 'EXPRESS_INTEREST') {
        state.assistant.status = 'awaiting_selection';
      } else {
        state.assistant.status = 'awaiting_selection';
      }
    },

    toggleOptionSelection: (state, action: PayloadAction<string>) => {
      if (!state.interrupt) return;

      const optionId = action.payload;
      const selectedIds = state.interrupt.selectedOptionIds;

      // For CONFIRMATION, only one option can be selected (replace existing)
      if (state.interrupt.eventType === 'CONFIRMATION') {
        state.interrupt.selectedOptionIds = [optionId];
        return;
      }

      // For MULTIPLE_CHOICE, always use multi-select mode (toggle selection)
      // This allows users to select multiple suggested edits
      if (selectedIds.includes(optionId)) {
        state.interrupt.selectedOptionIds = selectedIds.filter(id => id !== optionId);
      } else {
        state.interrupt.selectedOptionIds.push(optionId);
      }
    },

    selectAllOptions: state => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;
      if (!state.interrupt.options) return;

      // Select all option IDs
      state.interrupt.selectedOptionIds = state.interrupt.options.map(opt => opt.id);
    },

    deselectAllOptions: state => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;

      state.interrupt.selectedOptionIds = [];
    },

    // MULTI_STEP interrupt actions
    setStepResponse: (
      state,
      action: PayloadAction<{
        stepId: string;
        selectedIds?: string[];
        confirmed?: boolean;
      }>,
    ) => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTI_STEP') return;

      const { stepId, selectedIds, confirmed } = action.payload;
      if (!state.interrupt.stepResponses) {
        state.interrupt.stepResponses = {};
      }

      state.interrupt.stepResponses[stepId] = {
        stepId,
        selectedIds,
        confirmed,
      };
    },

    setCurrentStepIndex: (state, action: PayloadAction<number>) => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTI_STEP') return;

      state.interrupt.currentStepIndex = action.payload;
    },

    clearInterrupt: state => {
      state.interrupt = null;
    },

    // Copy selection management
    toggleCopySectionSelection: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      const selectedIds = state.selectedSectionIdsForCopy;

      if (selectedIds.includes(sectionId)) {
        state.selectedSectionIdsForCopy = selectedIds.filter(id => id !== sectionId);
      } else {
        state.selectedSectionIdsForCopy.push(sectionId);
      }
    },

    clearCopySectionSelection: state => {
      state.selectedSectionIdsForCopy = [];
    },

    selectAllSectionsForCopy: (state, action: PayloadAction<string[]>) => {
      state.selectedSectionIdsForCopy = action.payload;
    },

    // UI state management
    setChatVisibility: (state, action: PayloadAction<boolean>) => {
      state.isChatVisible = action.payload;
    },

    toggleChatVisibility: state => {
      state.isChatVisible = !state.isChatVisible;
    },

    setChatOnboardingVisibility: (state, action: PayloadAction<boolean>) => {
      state.isChatOnboardingVisible = action.payload;
    },

    dismissChatOnboarding: state => {
      state.isChatOnboardingVisible = false;
    },

    // Focused section management (for editing banner in sidebar)
    setFocusedSection: (
      state,
      action: PayloadAction<{ id: string; name: string } | null>,
    ) => {
      state.focusedSection = action.payload;
    },

    clearFocusedSection: state => {
      state.focusedSection = null;
    },

    // Workflow completion
    completeWorkflow: state => {
      state.assistant.status = 'idle';
      state.assistant.currentStep = null;
      state.interrupt = null;
      // Clear streaming sections since workflow is done
      state.streamingSections = [];
      // NOTE: sectionUpdates, affectedSectionIds, and sectionPreviousContent persist
      // for user review/undo. They are cleared when user approves/reverts/edits each section.
    },

    // Error handling
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.assistant.status = 'idle';
      // Clear section updates on error to prevent stuck content
      state.sectionUpdates = {};
      state.streamingSections = [];
    },

    clearError: state => {
      state.error = null;
    },

    // SSE performance tracking
    setSSEPerformanceMetrics: (state, action: PayloadAction<SSEPerformanceMetrics>) => {
      state.ssePerformanceMetrics = action.payload;
    },

    clearSSEPerformanceMetrics: state => {
      state.ssePerformanceMetrics = null;
    },

    // Reset
    resetChat: () => initialState,
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  initializeChat,
  setThreadId,
  addUserMessage,
  setAssistantStatus,
  setCurrentStep,
  setActivitySnapshot,
  clearActivity,
  clearAllActivities,
  startStreamingMessage,
  appendStreamingContent,
  finalizeMessage,
  startSectionStreaming,
  appendSectionContent,
  finalizeSectionUpdate,
  finalizeSectionStreaming,
  clearAllSectionUpdates,
  saveSectionSnapshot,
  markSectionAffected,
  clearAffectedSection,
  clearSectionSnapshot,
  setDirtySection,
  clearDirtySection,
  clearAllDirtySections,
  showInterrupt,
  toggleOptionSelection,
  selectAllOptions,
  deselectAllOptions,
  setStepResponse,
  setCurrentStepIndex,
  clearInterrupt,
  toggleCopySectionSelection,
  clearCopySectionSelection,
  selectAllSectionsForCopy,
  setChatVisibility,
  toggleChatVisibility,
  setChatOnboardingVisibility,
  dismissChatOnboarding,
  setFocusedSection,
  clearFocusedSection,
  completeWorkflow,
  setError,
  clearError,
  setSSEPerformanceMetrics,
  clearSSEPerformanceMetrics,
  resetChat,
} = noteAiEditSlice.actions;

export default noteAiEditSlice.reducer;
